import { ApolloCache } from 'apollo-cache';
import { InMemoryCache, NormalizedCacheObject } from 'apollo-cache-inmemory';
import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { toIdValue } from 'apollo-utilities';
import { Activity, StravaResolution } from '../components/Strava/types';
import { DEFAULT_RESOLUTION } from '../components/Strava/withActivityStreams';
import { configureLocalStateLink } from './configureApolloLocalState';
import configureErrorLink from './configureErrorLink';

let apolloClient: ApolloClient<any>;

const dataIdFromObject = ({
    id,
    __typename
}: {
    id?: any;
    __typename?: string;
}): string | null => {
    if (__typename && typeof id !== 'undefined') {
        return `${__typename}:${id}`;
    }
    return null;
};

const create = (link?: ApolloLink) => {
    if (!link) {
        throw new Error('No apollo link has been provided');
    }

    let cache: ApolloCache<NormalizedCacheObject> = new InMemoryCache({
        cacheRedirects: {
            Activity: {
                lngLatStream: (
                    activity: Activity,
                    { resolution }: { resolution: StravaResolution }
                ) => {
                    const { id } = activity;
                    const compositeId = `${id}:${resolution}:lnglat`;
                    const dataId = dataIdFromObject({
                        __typename: 'LngLatStream',
                        id: compositeId
                    });
                    return dataId ? toIdValue(dataId) : null;
                },
                scalarStreams: (
                    activity: Activity,
                    {
                        resolution,
                        streamTypes
                    }: { resolution: string; streamTypes: string[] }
                ) =>
                    streamTypes.map(t => {
                        const { id } = activity;
                        const compositeId = `${id}:${resolution}:${t}`;
                        const dataId = dataIdFromObject({
                            __typename: 'ScalarStream',
                            id: compositeId
                        });

                        return dataId ? toIdValue(dataId) : null;
                    })
            },
            DemoActivity: {
                lngLatStream: (activity: Activity) => {
                    const { id } = activity;
                    const compositeId = `${id}:${DEFAULT_RESOLUTION}:lnglat`;
                    const dataId = dataIdFromObject({
                        __typename: 'LngLatStream',
                        id: compositeId
                    });
                    return dataId ? toIdValue(dataId) : null;
                },
                scalarStreams: (
                    activity: Activity,
                    { streamTypes }: { streamTypes: string[] }
                ) =>
                    streamTypes.map(t => {
                        const { id } = activity;
                        const compositeId = `${id}:${DEFAULT_RESOLUTION}:${t}`;
                        const dataId = dataIdFromObject({
                            __typename: 'ScalarStream',
                            id: compositeId
                        });

                        return dataId ? toIdValue(dataId) : null;
                    })
            },
            Query: {
                activity: (_: any, props: { input: { id: string } }) => {
                    const dataId = dataIdFromObject({
                        __typename: 'ActivityPayload',
                        id: props.input.id
                    });
                    return dataId ? toIdValue(dataId) : null;
                },
                demoActivity: (_: any, props: { input: { id: string } }) => {
                    const dataId = dataIdFromObject({
                        __typename: 'DemoActivityPayload',
                        id: props.input.id
                    });
                    return dataId ? toIdValue(dataId) : null;
                }
            }
        },
        dataIdFromObject
    });

    // Get the preloaded page state, if available
    const state = (process as any).browser && (window as any).__APOLLO_STATE__;

    if (state) {
        // Clean up the preloaded state
        delete (window as any).__APOLLO_STATE__;
        // Load the state into the apollo cache
        cache = cache.restore(state);
    }

    return new ApolloClient({
        cache,
        link: ApolloLink.from([
            configureErrorLink(cache),
            configureLocalStateLink(cache),
            link
        ]),
        ssrMode: !(process as any).browser
    });
};

export default (link?: ApolloLink): ApolloClient<any> => {
    if (!(process as any).browser) {
        return create(link);
    }

    if (!apolloClient) {
        apolloClient = create(link);
    }
    return apolloClient;
};



// WEBPACK FOOTER //
// ./src/client/apollo/configureApolloClient.tsx